import { get, cloneDeep } from '@yakit/core/objectz'
import growl from '@yakit/ui/growl'
import problemHandler from '@yakit/ui/growl/problemHandler'
import { tempLinkDownloader } from '~/shared/downloader/blobDownloader'
import { getUser } from '~/user'
import CreateEditInfo from '~/common/CreatedInfo.svelte'
import { f7 } from 'framework7-svelte'
import { isCM, isPayment } from '../uitls/tranType'

/**
 * Customer Dash info endpoints
 */
export function createTranShowController({ user, dataApiFactory, appState }) {
  // $: card1 = transformFields($user.isCustomer ? custConfig.card1 : config.card1)
  // $: card2 = transformFields($user.isCustomer ? custConfig.card2 : config.card2)
  const dataApi = dataApiFactory.ar.tran
  const adJustApi = dataApiFactory.ar.adjust
  const payTranHeader = getPayTranHeader()
  const memoTranHeader = getMemoTranHeader()
  const invTranHeader = getMemoTranHeader(user)

  const disputePopoverSchema = getDisputePopoverSchema()

  const reverse = async (tran) => {
    const results = await dataApi.rpc('reverse', { ids: [tran.id] })
    results.ok ? growl.success(results.title) : problemHandler.handleError(results)
    return results
  }

  const checkImage = async (tran) => {
    const name = get(tran, 'autoCash.imageFileName')
    let path = `autocash/download?name=${name}`
    dataApi
      .ky(path)
      .then((r) => r.blob())
      .then((blob) => {
        const name = path.split('/').slice(-1)[0]
        tempLinkDownloader(blob, name)
      })
  }

  const glVoid = async (tran) => {
    const results = await dataApi.rpc('glVoid', { ids: [tran.id] })
    results.ok ? growl.success(results.title) : problemHandler.handleError(results)
    return results
  }

  const goToAdjust = async (tran) => {
    f7.views.main.router.navigate(`/ar/trans/${tran.id}/adjust`)
  }

  return {
    payTranHeader,
    memoTranHeader,
    invTranHeader,
    disputePopoverSchema,
    reverse,
    checkImage,
    glVoid,
    goToAdjust,
    popverId
  }
}
const popverId = 'actions-popover'

/**
 * Overlay data-dependent visibility and Svelte-only wiring on show.toolbarOptions from REST config.
 * Role filtering is already done in AppConfigController. Do not check isAdmin / isAutocashManager here.
 */
export function overlayTranShowToolbar(serverOpts, tran) {
  const opts = cloneDeep(serverOpts || { leftButtons: {}, rightButtons: {} })
  opts.leftButtons = opts.leftButtons || {}
  opts.rightButtons = opts.rightButtons || {}

  if (opts.rightButtons.createUpdate && tran) {
    opts.rightButtons.createUpdate.component = CreateEditInfo
    opts.rightButtons.createUpdate.params = { entityId: tran.id, entityType: 'ar/tran' }
  }
  if (opts.leftButtons.goToAdjust && !isCM(tran)) {
    opts.leftButtons.goToAdjust.enabled = false
  }
  if (opts.leftButtons.glVoid && !(isPayment(tran) && tran.state === 0)) {
    opts.leftButtons.glVoid.enabled = false
  }
  if (opts.leftButtons.checkImage && !(tran?.docType === 'PA' && get(tran, 'autoCash.imageFileName'))) {
    opts.leftButtons.checkImage.enabled = false
  }
  return opts
}

export function getMemoTranHeader(user) {
  const config = {
    card1: [
      { key: 'dueDate', format: 'date' },
      { label: 'Trans Date', key: 'tranDate', format: 'date' },
      { label: 'Trans Type', key: 'tranType.name' },
      { label: 'PO Num', key: 'ponum' },
      //{ label: 'Created By Payment', key:'autoCash.createdByPayNum'},
      { label: 'Trans Num', key: 'refnum' }, //till we add it to the header
      { label: 'SO Num', key: 'refnum2' },
      { label: 'Orig Trans Num', key: 'origRefnum' },
      { label: 'Created By ArBatch', key: 'createdArBatch.id' },
      { label: 'Comments', key: 'comments' }
    ],
    card2: [
      { label: 'Balance', key: 'amount', format: 'money', class: 'font-bold' }, //RNDC custom (Balance Amount)
      { label: 'Orig Amount', key: 'origAmount', format: 'money', class: 'font-bold' },
      { label: 'Status', key: 'status.code' },
      { label: 'Term', key: 'term.name' },
      { label: 'Gross Amount', key: 'flex.num1', format: 'money' }, //RNDC custom (Alpha only))
      { label: 'Tax Amount', key: 'lineSum.taxAmount', format: 'money' }, //RNDC custom (Alpha only))
      { label: 'Disc Amount', key: 'flex.num2', format: 'money' }, //RNDC custom (Alpha only))
      //    { key:'state' },
      { label: 'Order Type', key: 'flex.text1' }, //RNDC custom
      { label: 'Bill To Loc', key: 'flex.text4' }, //RNDC custom
      { label: 'Misc. Ref Num', key: 'flex.text2' } //RNDC custom
    ]
  }
  //hack for cust for now
  return getUser().isCustomer ? custConfig : config
}

export function getPayTranHeader() {
  const payTranHeader = {
    card1: [
      { label: 'Date', key: 'tranDate', format: 'date' },
      { label: 'Type', key: 'tranType.name' },
      { label: 'Num', key: 'refnum' }, //till we add it to the header
      // { label: 'SO Num', key:'refnum2'},
      // { label: 'Orig Trans Num', key:'origRefnum'},

      { label: 'Comments', key: 'comments', itemClass: 'hide-if-null' }
    ],
    card2: [
      { label: 'Amount', key: 'origAmount', format: 'money-abs$', class: 'font-bold' },
      { label: 'Unapplied', key: 'amount', format: 'money-abs$' }, //RNDC custom (Balance Amount)
      { label: 'Status', key: 'status.code' },
      { label: 'Source Batch', key: 'createdArBatchId', itemClass: 'hide-if-null' }
    ]
  }
  return payTranHeader
}

export function getDisputePopoverSchema() {
  let editSchema = {
    'dispute.reason': {
      input: 'select',
      options: {
        isValueObject: true,
        dataApi: {
          key: 'ar.reason',
          q: {
            $or: [{ restrictUpdate: '$isNull' }, { restrictUpdate: 2 }]
          }
        }
      }
    },
    'dispute.amount': {
      type: 'number',
      format: 'money'
    },
    'dispute.note': {
      maxLength: 1000
    }
  }
  return editSchema
}

export const custConfig = {
  card1: [
    { key: 'dueDate', format: 'date' },
    { label: 'Trans Date', key: 'tranDate', format: 'date' },
    { label: 'Trans Type', key: 'tranType.name' },
    { label: 'PO Num', key: 'ponum' }
    //{ label: 'Created By Payment', key:'autoCash.createdByPayNum'},
    // { label: 'Trans Num', key:'refnum'},  //till we add it to the header
  ],
  card2: [
    { label: 'Balance', key: 'amount', format: 'money$' }, //RNDC custom (Balance Amount)
    { label: 'Orig Amount', key: 'origAmount', format: 'money$' },
    //  { label: 'Gross Amount', key:'flex.num1', type: 'amount', input: 'amount'},    //RNDC custom (Alpha only))
    //  { label: 'Disc Amount', key:'discAmount', type: 'amount', input: 'amount'}, //RNDC custom (Alpha only))
    //    { key:'state' },
    { label: 'Status', key: 'status.code' },
    { label: 'Terms', key: 'term.name' }
  ]
}

